import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { WEAPONS, type WeaponId } from '../core/config';
import type { RecoilKick } from '../gameplay/weapon';
import { WEAPON_MODELS, getWeaponsByPriority } from '../gameplay/weapon-models';
import type { WeaponSkinLoader } from '../cosmetics/weapon-skin-loader';
import type { WeaponLoadout } from '../cosmetics/weapon-skin-types';
import { weaponSkinRegistry } from '../cosmetics/weapon-skin-registry';
import { WeaponInspector } from '../cosmetics/weapon-inspect';
import { StatTrakDisplay } from '../cosmetics/stattrak-display';
import { StickerRenderer } from '../cosmetics/sticker-renderer';
import { STICKER_MANIFEST } from '../cosmetics/sticker-types';

// ---------------------------------------------------------------------------
// First-person weapon viewmodel — two-pass rendering with separate scene
// Prevents wall clipping by using clearDepth between world and viewmodel.
// ---------------------------------------------------------------------------

/** Offset from camera: right-hand side, lower portion. */
const VIEW_OFFSET = new THREE.Vector3(0.28, -0.24, -0.45);

// Animation constants
const BOB_SPEED = 10;
const BOB_AMOUNT_X = 0.005;
const BOB_AMOUNT_Y = 0.008;
const SWITCH_SPEED = 4;
const SWITCH_DROP = 0.25;
const SWAY_SPEED_X = 1.5;
const SWAY_SPEED_Y = 2.0;
const SWAY_AMOUNT_X = 0.003;
const SWAY_AMOUNT_Y = 0.002;

// Jump/land constants
const AIRBORNE_DROP = 0.03;
const AIRBORNE_TILT = 0.02;
const LAND_IMPACT_Y = -0.06;
const LAND_RECOVER_DURATION = 0.2;

// Crouch constants
const CROUCH_LERP_RATE = 8;
const CROUCH_DROP_Y = 0.04;
const CROUCH_FORWARD_Z = -0.02;

// Slide constants
const SLIDE_LERP_RATE = 10;
const SLIDE_TILT_Z = 0.21;  // ~12 degrees
const SLIDE_DROP_Y = 0.06;
const SLIDE_SHIFT_X = -0.03;

// Grenade throw animation constants
const GRENADE_PULLBACK_DURATION = 0.12;  // Wind-up phase
const GRENADE_FORWARD_DURATION = 0.15;   // Throw forward phase
const GRENADE_FOLLOW_DURATION = 0.20;    // Follow-through after release
const GRENADE_PULLBACK_Z = 0.08;         // How far hand pulls back
const GRENADE_PULLBACK_Y = 0.04;         // How far hand lifts
const GRENADE_PULLBACK_ROT_X = 0.15;     // Tilt back during wind-up
const GRENADE_FORWARD_Z = -0.18;         // How far hand lunges forward
const GRENADE_FORWARD_Y = 0.06;          // Hand rises during throw
const GRENADE_FORWARD_ROT_X = -0.25;     // Tilt forward during throw

/** Smooth hermite interpolation: t*t*(3 - 2*t) */
function smoothstep(t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return c * c * (3 - 2 * c);
}

/** Linear interpolation. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// ---------------------------------------------------------------------------
// Reload animation phase timings per weapon type
// Each entry: [phaseEndTime, ...] — 5 phases
// ---------------------------------------------------------------------------
export type WeaponCategory = 'pistol' | 'rifle' | 'sniper' | 'melee' | 'smg' | 'shotgun';

const VIEWMODEL_PART_KEY = 'viewmodelPart';
const VIEWMODEL_ARM_PART = 'arm';
const LEGACY_GLB_SCALE_THRESHOLD = 0.05;
const TARGET_MODEL_LONGEST_EDGE: Record<WeaponCategory, number> = {
  pistol: 0.34,
  rifle: 0.68,
  sniper: 0.78,
  melee: 0.42,
  smg: 0.52,
  shotgun: 0.70,
};

export function isViewmodelArmPart(obj: { userData?: Record<string, unknown> }): boolean {
  return obj.userData?.[VIEWMODEL_PART_KEY] === VIEWMODEL_ARM_PART;
}

function getLongestAxis(bounds: THREE.Vector3): 'x' | 'y' | 'z' {
  if (bounds.x >= bounds.y && bounds.x >= bounds.z) return 'x';
  if (bounds.y >= bounds.x && bounds.y >= bounds.z) return 'y';
  return 'z';
}

export function resolveWeaponModelScale(
  id: WeaponId,
  bounds: THREE.Vector3,
  configuredScale: number,
): number {
  if (configuredScale >= LEGACY_GLB_SCALE_THRESHOLD) {
    return configuredScale;
  }

  const longestEdge = Math.max(bounds.x, bounds.y, bounds.z);
  if (longestEdge <= 0) return configuredScale;

  const weaponType = WEAPONS[id]?.type ?? 'rifle';
  return TARGET_MODEL_LONGEST_EDGE[weaponType] / longestEdge;
}

export function resolveWeaponModelRotation(
  bounds: THREE.Vector3,
  configuredRotation: [number, number, number],
): [number, number, number] {
  if (Math.abs(configuredRotation[1] - Math.PI) < 1e-4 && getLongestAxis(bounds) === 'x') {
    // Model barrel extends along +X; rotate +PI/2 to align with -Z (camera forward)
    return [configuredRotation[0], Math.PI / 2, configuredRotation[2]];
  }
  return configuredRotation;
}

const RELOAD_TIMINGS: Record<WeaponCategory, number[]> = {
  pistol:  [0.3, 0.8, 1.2, 1.6, 1.8],
  rifle:   [0.4, 0.9, 1.5, 2.0, 2.3],
  sniper:  [0.5, 1.5, 2.5, 3.2, 3.5],
  melee:   [0, 0, 0, 0, 0], // knife has no reload
  smg:     [0.3, 0.7, 1.2, 1.7, 2.0],
  shotgun: [0.2, 0.4, 0.6, 0.8, 1.0],
};

// Colors — arm/glove colors are mutable so they adapt to selected skin
let COL_SKIN = 0xd4a574;
let COL_GLOVE = 0x2a2a2a;
let COL_GLOVE_PALM = 0x1e1e1e;
let COL_SLEEVE = 0x3a3a2a; // olive drab sleeve
const COL_METAL_DARK = 0x1a1a1a;
const COL_METAL = 0x222222;
const COL_METAL_MID = 0x333333;
const COL_METAL_LIGHT = 0x444444;
const COL_RECEIVER = 0x3a3a3a;
const COL_WOOD_AK = 0x5a3a1a;
const COL_WOOD_AWP = 0x2a4a1a;
const COL_GRIP = 0x2a2a2a;
const COL_MAG = 0x292929;

function mat(color: number): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color, flatShading: true });
}

/** Create a mesh, position it, and add to group. */
function part(
  g: THREE.Group,
  geo: THREE.BufferGeometry,
  color: number,
  x: number, y: number, z: number,
  rx = 0, ry = 0, rz = 0,
): THREE.Mesh {
  const m = new THREE.Mesh(geo, mat(color));
  m.position.set(x, y, z);
  if (rx || ry || rz) m.rotation.set(rx, ry, rz);
  g.add(m);
  return m;
}

function tagNewChildren(group: THREE.Group, startIndex: number, partKind: string): void {
  for (let i = startIndex; i < group.children.length; i++) {
    group.children[i].userData[VIEWMODEL_PART_KEY] = partKind;
  }
}

function disposeViewmodelChild(child: THREE.Object3D): void {
  child.traverse((node) => {
    if (!(node instanceof THREE.Mesh)) return;
    node.geometry.dispose();
    if (Array.isArray(node.material)) {
      node.material.forEach((material) => material.dispose());
    } else if (node.material instanceof THREE.Material) {
      node.material.dispose();
    }
  });
}

function removeWeaponChildren(group: THREE.Group): void {
  const childrenToRemove = [...group.children].filter((child) => !isViewmodelArmPart(child));
  for (const child of childrenToRemove) {
    group.remove(child);
    disposeViewmodelChild(child);
  }
}

// ---------------------------------------------------------------------------
// Hand / arm builders — shared across all weapons
// ---------------------------------------------------------------------------

/**
 * Build a right hand + forearm group. Positioned at the grip/trigger area.
 * Returns a group that the caller positions on the weapon.
 */
function buildRightHand(g: THREE.Group, x: number, y: number, z: number): void {
  const startIndex = g.children.length;
  // Forearm (sleeve)
  part(g, new THREE.BoxGeometry(0.06, 0.055, 0.16), COL_SLEEVE, x + 0.04, y - 0.04, z + 0.12);
  // Wrist (skin)
  part(g, new THREE.BoxGeometry(0.05, 0.045, 0.04), COL_SKIN, x + 0.04, y - 0.03, z + 0.04);
  // Hand/glove — wraps around grip
  part(g, new THREE.BoxGeometry(0.05, 0.06, 0.055), COL_GLOVE, x + 0.035, y - 0.03, z + 0.0);
  // Thumb
  part(g, new THREE.BoxGeometry(0.02, 0.025, 0.04), COL_GLOVE, x + 0.065, y - 0.01, z - 0.01);
  // Finger curled on trigger
  part(g, new THREE.BoxGeometry(0.015, 0.025, 0.03), COL_GLOVE, x + 0.025, y - 0.04, z - 0.03);
  // Knuckles detail
  part(g, new THREE.BoxGeometry(0.048, 0.015, 0.02), COL_GLOVE_PALM, x + 0.035, y - 0.005, z - 0.02);
  tagNewChildren(g, startIndex, VIEWMODEL_ARM_PART);
}

/**
 * Build a left hand + forearm (support hand on barrel/foregrip).
 */
function buildLeftHand(g: THREE.Group, x: number, y: number, z: number): void {
  const startIndex = g.children.length;
  // Forearm (sleeve)
  part(g, new THREE.BoxGeometry(0.06, 0.055, 0.16), COL_SLEEVE, x - 0.04, y - 0.04, z + 0.12);
  // Wrist (skin)
  part(g, new THREE.BoxGeometry(0.05, 0.045, 0.04), COL_SKIN, x - 0.04, y - 0.03, z + 0.04);
  // Hand/glove — wraps around barrel/handguard
  part(g, new THREE.BoxGeometry(0.05, 0.055, 0.06), COL_GLOVE, x - 0.035, y - 0.02, z + 0.0);
  // Thumb on top
  part(g, new THREE.BoxGeometry(0.02, 0.02, 0.04), COL_GLOVE, x - 0.065, y + 0.01, z - 0.01);
  // Knuckles
  part(g, new THREE.BoxGeometry(0.048, 0.015, 0.02), COL_GLOVE_PALM, x - 0.035, y + 0.005, z - 0.02);
  tagNewChildren(g, startIndex, VIEWMODEL_ARM_PART);
}

/**
 * Build a left hand specifically for pistol (cupping grip).
 */
function buildLeftHandPistol(g: THREE.Group, x: number, y: number, z: number): void {
  const startIndex = g.children.length;
  // Forearm
  part(g, new THREE.BoxGeometry(0.06, 0.05, 0.14), COL_SLEEVE, x - 0.04, y - 0.04, z + 0.10);
  // Wrist
  part(g, new THREE.BoxGeometry(0.05, 0.04, 0.035), COL_SKIN, x - 0.04, y - 0.03, z + 0.03);
  // Hand wrapping around the right hand on grip
  part(g, new THREE.BoxGeometry(0.055, 0.055, 0.05), COL_GLOVE, x - 0.035, y - 0.03, z - 0.0);
  // Thumb over
  part(g, new THREE.BoxGeometry(0.02, 0.02, 0.04), COL_GLOVE, x - 0.065, y - 0.01, z - 0.01);
  tagNewChildren(g, startIndex, VIEWMODEL_ARM_PART);
}

// ---------------------------------------------------------------------------
// Weapon mesh builders (detailed with hands)
// ---------------------------------------------------------------------------

function buildPistolMesh(): THREE.Group {
  const g = new THREE.Group();

  // ---- Barrel ----
  part(g, new THREE.BoxGeometry(0.025, 0.02, 0.08), COL_METAL_DARK, 0, 0.025, -0.18);
  // Barrel tip
  part(g, new THREE.CylinderGeometry(0.008, 0.008, 0.03, 6), COL_METAL, 0, 0.025, -0.23, Math.PI / 2, 0, 0);

  // ---- Slide (top half of pistol) ----
  part(g, new THREE.BoxGeometry(0.032, 0.03, 0.16), COL_METAL, 0, 0.03, -0.10);
  // Slide serrations (rear)
  part(g, new THREE.BoxGeometry(0.034, 0.015, 0.03), COL_METAL_DARK, 0, 0.04, -0.01);
  // Front sight
  part(g, new THREE.BoxGeometry(0.008, 0.012, 0.006), COL_METAL_DARK, 0, 0.05, -0.16);
  // Rear sight
  part(g, new THREE.BoxGeometry(0.02, 0.012, 0.006), COL_METAL_DARK, 0, 0.05, -0.04);

  // ---- Frame / receiver ----
  part(g, new THREE.BoxGeometry(0.036, 0.025, 0.12), COL_METAL_MID, 0, 0.005, -0.08);

  // ---- Trigger guard ----
  part(g, new THREE.BoxGeometry(0.025, 0.015, 0.04), COL_METAL_MID, 0, -0.015, -0.06);
  // Trigger
  part(g, new THREE.BoxGeometry(0.008, 0.018, 0.008), COL_METAL_DARK, 0, -0.008, -0.05);

  // ---- Grip ----
  part(g, new THREE.BoxGeometry(0.035, 0.08, 0.04), COL_METAL_LIGHT, 0, -0.04, -0.01);
  // Grip texture panels (slightly wider)
  part(g, new THREE.BoxGeometry(0.038, 0.06, 0.005), COL_GRIP, 0, -0.04, 0.01);
  part(g, new THREE.BoxGeometry(0.038, 0.06, 0.005), COL_GRIP, 0, -0.04, -0.03);

  // ---- Magazine (visible at bottom of grip) ----
  part(g, new THREE.BoxGeometry(0.028, 0.03, 0.032), COL_MAG, 0, -0.09, -0.01);
  // Mag baseplate
  part(g, new THREE.BoxGeometry(0.032, 0.008, 0.036), COL_METAL_DARK, 0, -0.108, -0.01);

  // ---- Hammer ----
  part(g, new THREE.BoxGeometry(0.01, 0.015, 0.01), COL_METAL_DARK, 0, 0.02, 0.02);

  // ---- Hands ----
  buildRightHand(g, -0.01, -0.03, 0.0);
  buildLeftHandPistol(g, 0.01, -0.03, 0.0);

  return g;
}

function buildRifleMesh(): THREE.Group {
  const g = new THREE.Group();

  // ---- Barrel ----
  part(g, new THREE.BoxGeometry(0.02, 0.02, 0.22), COL_METAL_DARK, 0, 0.03, -0.36);
  // Barrel tip / flash hider
  part(g, new THREE.BoxGeometry(0.025, 0.025, 0.03), COL_METAL, 0, 0.03, -0.48);
  // Front sight post
  part(g, new THREE.BoxGeometry(0.008, 0.03, 0.008), COL_METAL_DARK, 0, 0.055, -0.42);
  // Front sight base
  part(g, new THREE.BoxGeometry(0.025, 0.01, 0.02), COL_METAL_DARK, 0, 0.04, -0.42);

  // ---- Gas tube (above barrel) ----
  part(g, new THREE.BoxGeometry(0.015, 0.015, 0.18), COL_METAL_MID, 0, 0.05, -0.30);

  // ---- Handguard ----
  part(g, new THREE.BoxGeometry(0.045, 0.04, 0.16), COL_WOOD_AK, 0, 0.02, -0.24);
  // Handguard bottom rail
  part(g, new THREE.BoxGeometry(0.035, 0.008, 0.14), COL_METAL_MID, 0, -0.002, -0.24);

  // ---- Receiver (main body) ----
  part(g, new THREE.BoxGeometry(0.05, 0.055, 0.20), COL_RECEIVER, 0, 0.04, -0.06);
  // Receiver top cover
  part(g, new THREE.BoxGeometry(0.04, 0.015, 0.18), COL_METAL_MID, 0, 0.072, -0.06);
  // Rear sight
  part(g, new THREE.BoxGeometry(0.025, 0.02, 0.015), COL_METAL_DARK, 0, 0.085, 0.02);
  // Ejection port
  part(g, new THREE.BoxGeometry(0.005, 0.025, 0.04), COL_METAL_DARK, 0.028, 0.05, -0.04);
  // Charging handle
  part(g, new THREE.BoxGeometry(0.015, 0.012, 0.025), COL_METAL_DARK, 0.03, 0.065, -0.02);

  // ---- Magazine (curved AK-style) ----
  part(g, new THREE.BoxGeometry(0.03, 0.10, 0.04), COL_MAG, 0, -0.04, -0.05, 0.15, 0, 0);
  // Mag lip
  part(g, new THREE.BoxGeometry(0.032, 0.015, 0.042), COL_METAL_DARK, 0, 0.008, -0.05);

  // ---- Pistol grip ----
  part(g, new THREE.BoxGeometry(0.032, 0.075, 0.04), COL_GRIP, 0, -0.04, 0.04, 0.2, 0, 0);
  // Grip bottom cap
  part(g, new THREE.BoxGeometry(0.034, 0.01, 0.042), COL_METAL_DARK, 0, -0.078, 0.05);

  // ---- Stock ----
  part(g, new THREE.BoxGeometry(0.04, 0.05, 0.16), COL_WOOD_AK, 0, 0.025, 0.14);
  // Stock butt plate
  part(g, new THREE.BoxGeometry(0.042, 0.055, 0.01), COL_METAL_DARK, 0, 0.025, 0.225);
  // Stock upper tang
  part(g, new THREE.BoxGeometry(0.03, 0.02, 0.06), COL_WOOD_AK, 0, 0.05, 0.06);

  // ---- Hands ----
  // Right hand on pistol grip
  buildRightHand(g, -0.01, -0.03, 0.04);
  // Left hand on handguard
  buildLeftHand(g, 0.01, 0.0, -0.22);

  return g;
}

function buildSniperMesh(): THREE.Group {
  const g = new THREE.Group();

  // ---- Long barrel ----
  part(g, new THREE.BoxGeometry(0.018, 0.018, 0.45), COL_METAL_DARK, 0, 0.03, -0.38);
  // Barrel tip / muzzle brake
  part(g, new THREE.BoxGeometry(0.024, 0.024, 0.04), COL_METAL, 0, 0.03, -0.62);
  part(g, new THREE.BoxGeometry(0.028, 0.028, 0.01), COL_METAL_DARK, 0, 0.03, -0.635);

  // ---- Receiver ----
  part(g, new THREE.BoxGeometry(0.05, 0.055, 0.24), COL_METAL_MID, 0, 0.04, -0.06);
  // Receiver top rail
  part(g, new THREE.BoxGeometry(0.035, 0.01, 0.22), COL_METAL_DARK, 0, 0.072, -0.06);

  // ---- Scope ----
  // Main tube
  part(g, new THREE.CylinderGeometry(0.018, 0.018, 0.18, 8), COL_METAL_DARK, 0, 0.095, -0.06, Math.PI / 2, 0, 0);
  // Front lens ring
  part(g, new THREE.CylinderGeometry(0.024, 0.024, 0.02, 8), COL_METAL, 0, 0.095, -0.15, Math.PI / 2, 0, 0);
  // Rear lens ring (eyepiece)
  part(g, new THREE.CylinderGeometry(0.022, 0.022, 0.02, 8), COL_METAL, 0, 0.095, 0.03, Math.PI / 2, 0, 0);
  // Scope mount front
  part(g, new THREE.BoxGeometry(0.03, 0.025, 0.02), COL_METAL_DARK, 0, 0.08, -0.10);
  // Scope mount rear
  part(g, new THREE.BoxGeometry(0.03, 0.025, 0.02), COL_METAL_DARK, 0, 0.08, -0.02);
  // Elevation turret
  part(g, new THREE.CylinderGeometry(0.01, 0.01, 0.02, 6), COL_METAL_MID, 0, 0.115, -0.06);
  // Windage turret
  part(g, new THREE.CylinderGeometry(0.01, 0.01, 0.02, 6), COL_METAL_MID, 0.03, 0.095, -0.06, 0, 0, Math.PI / 2);

  // ---- Bolt handle ----
  part(g, new THREE.BoxGeometry(0.01, 0.01, 0.05), COL_METAL_MID, 0.03, 0.055, 0.0);
  // Bolt knob
  part(g, new THREE.BoxGeometry(0.018, 0.018, 0.015), COL_METAL_DARK, 0.038, 0.055, 0.025);

  // ---- Handguard / barrel shroud ----
  part(g, new THREE.BoxGeometry(0.04, 0.035, 0.14), COL_WOOD_AWP, 0, 0.015, -0.22);

  // ---- Pistol grip ----
  part(g, new THREE.BoxGeometry(0.03, 0.07, 0.035), COL_GRIP, 0, -0.03, 0.04, 0.15, 0, 0);

  // ---- Trigger guard ----
  part(g, new THREE.BoxGeometry(0.025, 0.02, 0.05), COL_METAL_MID, 0, -0.015, 0.0);
  // Trigger
  part(g, new THREE.BoxGeometry(0.008, 0.02, 0.008), COL_METAL_DARK, 0, -0.01, -0.01);

  // ---- Stock ----
  part(g, new THREE.BoxGeometry(0.045, 0.07, 0.20), COL_WOOD_AWP, 0, 0.025, 0.18);
  // Cheek rest (raised portion)
  part(g, new THREE.BoxGeometry(0.04, 0.02, 0.10), COL_WOOD_AWP, 0, 0.065, 0.14);
  // Stock butt plate
  part(g, new THREE.BoxGeometry(0.048, 0.075, 0.012), COL_METAL_DARK, 0, 0.025, 0.285);
  // Stock upper tang
  part(g, new THREE.BoxGeometry(0.035, 0.02, 0.06), COL_WOOD_AWP, 0, 0.055, 0.06);

  // ---- Bipod (folded under barrel) ----
  // Left leg
  part(g, new THREE.BoxGeometry(0.005, 0.05, 0.008), COL_METAL_DARK, -0.015, -0.01, -0.32, 0.3, 0, 0);
  part(g, new THREE.BoxGeometry(0.005, 0.04, 0.008), COL_METAL_DARK, -0.015, -0.04, -0.35, -0.2, 0, 0);
  // Right leg
  part(g, new THREE.BoxGeometry(0.005, 0.05, 0.008), COL_METAL_DARK, 0.015, -0.01, -0.32, 0.3, 0, 0);
  part(g, new THREE.BoxGeometry(0.005, 0.04, 0.008), COL_METAL_DARK, 0.015, -0.04, -0.35, -0.2, 0, 0);
  // Bipod mount
  part(g, new THREE.BoxGeometry(0.025, 0.012, 0.02), COL_METAL_MID, 0, 0.005, -0.30);

  // ---- Magazine ----
  part(g, new THREE.BoxGeometry(0.028, 0.08, 0.035), COL_MAG, 0, -0.04, -0.04, 0.1, 0, 0);
  // Mag lip
  part(g, new THREE.BoxGeometry(0.03, 0.012, 0.037), COL_METAL_DARK, 0, 0.002, -0.04);

  // ---- Hands ----
  buildRightHand(g, -0.01, -0.025, 0.04);
  buildLeftHand(g, 0.01, -0.005, -0.20);

  return g;
}

function buildKnifeMesh(): THREE.Group {
  const g = new THREE.Group();

  // ---- Blade ----
  // Main blade body — flat, tapered
  part(g, new THREE.BoxGeometry(0.008, 0.025, 0.16), COL_METAL, 0, 0.02, -0.18);
  // Blade edge (thinner, darker)
  part(g, new THREE.BoxGeometry(0.003, 0.028, 0.15), COL_METAL_DARK, -0.004, 0.02, -0.175);
  // Blade spine (thicker top)
  part(g, new THREE.BoxGeometry(0.01, 0.008, 0.14), COL_METAL_MID, 0, 0.038, -0.17);
  // Blade tip (tapered triangle-ish)
  part(g, new THREE.BoxGeometry(0.006, 0.018, 0.03), COL_METAL, 0, 0.025, -0.275);
  // Blade fuller (groove)
  part(g, new THREE.BoxGeometry(0.003, 0.01, 0.08), COL_METAL_LIGHT, 0.002, 0.028, -0.19);

  // ---- Guard (crossguard between blade and handle) ----
  part(g, new THREE.BoxGeometry(0.035, 0.012, 0.015), COL_METAL_MID, 0, 0.015, -0.095);

  // ---- Handle / grip ----
  part(g, new THREE.BoxGeometry(0.022, 0.028, 0.08), COL_GRIP, 0, -0.005, -0.05);
  // Grip texture
  part(g, new THREE.BoxGeometry(0.024, 0.015, 0.06), 0x1a1a1a, 0, -0.005, -0.05);

  // ---- Pommel (end cap) ----
  part(g, new THREE.BoxGeometry(0.028, 0.018, 0.015), COL_METAL_DARK, 0, -0.005, -0.005);

  // ---- Right hand gripping the knife ----
  buildRightHand(g, -0.01, -0.02, -0.04);

  return g;
}

function buildGrenadeMesh(): THREE.Group {
  const g = new THREE.Group();

  // ---- Grenade body (cylindrical-ish) ----
  part(g, new THREE.CylinderGeometry(0.025, 0.025, 0.06, 8), 0x336633, 0, 0.0, -0.10);
  // Top cap
  part(g, new THREE.SphereGeometry(0.025, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), 0x336633, 0, 0.03, -0.10);
  // Spoon / lever
  part(g, new THREE.BoxGeometry(0.008, 0.005, 0.055), 0x888888, 0.018, 0.025, -0.10);
  // Pin ring
  part(g, new THREE.TorusGeometry(0.008, 0.002, 6, 8), 0xcccccc, 0, 0.04, -0.08);

  // ---- Right hand holding the grenade ----
  buildRightHand(g, -0.01, -0.02, -0.04);

  return g;
}

function buildSMGMesh(): THREE.Group {
  const g = new THREE.Group();

  // ---- Barrel (shorter than rifle) ----
  part(g, new THREE.BoxGeometry(0.018, 0.018, 0.14), COL_METAL_DARK, 0, 0.03, -0.28);
  // Barrel tip
  part(g, new THREE.CylinderGeometry(0.01, 0.01, 0.02, 6), COL_METAL, 0, 0.03, -0.36, Math.PI / 2, 0, 0);

  // ---- Receiver (compact) ----
  part(g, new THREE.BoxGeometry(0.042, 0.045, 0.16), COL_RECEIVER, 0, 0.035, -0.08);
  // Receiver top
  part(g, new THREE.BoxGeometry(0.035, 0.012, 0.14), COL_METAL_MID, 0, 0.062, -0.08);
  // Rear sight
  part(g, new THREE.BoxGeometry(0.02, 0.015, 0.01), COL_METAL_DARK, 0, 0.072, 0.01);
  // Front sight
  part(g, new THREE.BoxGeometry(0.008, 0.018, 0.006), COL_METAL_DARK, 0, 0.052, -0.26);
  // Ejection port
  part(g, new THREE.BoxGeometry(0.004, 0.02, 0.03), COL_METAL_DARK, 0.024, 0.045, -0.04);

  // ---- Handguard (short, compact) ----
  part(g, new THREE.BoxGeometry(0.038, 0.032, 0.10), COL_METAL_LIGHT, 0, 0.02, -0.18);

  // ---- Magazine (straight, SMG-style) ----
  part(g, new THREE.BoxGeometry(0.025, 0.08, 0.035), COL_MAG, 0, -0.03, -0.06);
  // Mag lip
  part(g, new THREE.BoxGeometry(0.027, 0.012, 0.037), COL_METAL_DARK, 0, 0.008, -0.06);

  // ---- Pistol grip ----
  part(g, new THREE.BoxGeometry(0.028, 0.065, 0.035), COL_GRIP, 0, -0.035, 0.03, 0.18, 0, 0);

  // ---- Folding stock (compact) ----
  part(g, new THREE.BoxGeometry(0.03, 0.035, 0.10), COL_METAL_MID, 0, 0.03, 0.12);
  // Stock butt
  part(g, new THREE.BoxGeometry(0.032, 0.04, 0.008), COL_METAL_DARK, 0, 0.03, 0.175);

  // ---- Hands ----
  buildRightHand(g, -0.01, -0.03, 0.03);
  buildLeftHand(g, 0.01, 0.0, -0.16);

  return g;
}

function buildShotgunMesh(): THREE.Group {
  const g = new THREE.Group();

  // ---- Barrel (wide, long) ----
  part(g, new THREE.BoxGeometry(0.028, 0.028, 0.30), COL_METAL_DARK, 0, 0.03, -0.32);
  // Barrel tip / muzzle
  part(g, new THREE.CylinderGeometry(0.016, 0.016, 0.02, 8), COL_METAL, 0, 0.03, -0.48, Math.PI / 2, 0, 0);
  // Front bead sight
  part(g, new THREE.SphereGeometry(0.005, 6, 4), COL_METAL_LIGHT, 0, 0.05, -0.44);

  // ---- Magazine tube (under barrel) ----
  part(g, new THREE.BoxGeometry(0.022, 0.022, 0.22), COL_METAL_MID, 0, 0.005, -0.28);
  // Tube cap
  part(g, new THREE.CylinderGeometry(0.013, 0.013, 0.015, 6), COL_METAL_DARK, 0, 0.005, -0.40, Math.PI / 2, 0, 0);

  // ---- Receiver ----
  part(g, new THREE.BoxGeometry(0.048, 0.05, 0.18), COL_RECEIVER, 0, 0.04, -0.06);
  // Receiver top
  part(g, new THREE.BoxGeometry(0.04, 0.012, 0.16), COL_METAL_MID, 0, 0.07, -0.06);
  // Ejection port
  part(g, new THREE.BoxGeometry(0.005, 0.03, 0.04), COL_METAL_DARK, 0.027, 0.05, -0.04);
  // Loading port (bottom)
  part(g, new THREE.BoxGeometry(0.03, 0.008, 0.06), COL_METAL_DARK, 0, 0.012, -0.06);

  // ---- Pump grip / forend ----
  part(g, new THREE.BoxGeometry(0.042, 0.038, 0.10), COL_METAL_LIGHT, 0, 0.015, -0.20);
  // Pump grip ridges
  part(g, new THREE.BoxGeometry(0.044, 0.008, 0.08), COL_GRIP, 0, 0.0, -0.20);

  // ---- Pistol grip ----
  part(g, new THREE.BoxGeometry(0.03, 0.07, 0.038), COL_GRIP, 0, -0.035, 0.04, 0.2, 0, 0);

  // ---- Trigger guard ----
  part(g, new THREE.BoxGeometry(0.025, 0.018, 0.045), COL_METAL_MID, 0, -0.01, 0.0);
  // Trigger
  part(g, new THREE.BoxGeometry(0.008, 0.02, 0.008), COL_METAL_DARK, 0, -0.005, -0.01);

  // ---- Stock ----
  part(g, new THREE.BoxGeometry(0.04, 0.05, 0.16), COL_WOOD_AK, 0, 0.03, 0.14);
  // Stock butt plate
  part(g, new THREE.BoxGeometry(0.042, 0.055, 0.01), COL_METAL_DARK, 0, 0.03, 0.225);

  // ---- Hands ----
  buildRightHand(g, -0.01, -0.03, 0.04);
  buildLeftHand(g, 0.01, -0.005, -0.18);

  return g;
}

/** Build a scoped rifle mesh (AUG/SG553): rifle base + small scope on top */
function buildScopedRifleMesh(): THREE.Group {
  const g = buildRifleMesh();

  // ---- Scope (smaller than sniper scope — 2.5x optic) ----
  // Main tube
  part(g, new THREE.CylinderGeometry(0.012, 0.012, 0.10, 8), COL_METAL_DARK, 0, 0.09, -0.06, Math.PI / 2, 0, 0);
  // Front lens ring
  part(g, new THREE.CylinderGeometry(0.016, 0.016, 0.015, 8), COL_METAL, 0, 0.09, -0.11, Math.PI / 2, 0, 0);
  // Rear lens ring (eyepiece)
  part(g, new THREE.CylinderGeometry(0.015, 0.015, 0.015, 8), COL_METAL, 0, 0.09, -0.01, Math.PI / 2, 0, 0);
  // Scope mount
  part(g, new THREE.BoxGeometry(0.025, 0.018, 0.015), COL_METAL_DARK, 0, 0.078, -0.06);

  return g;
}

/** Build a silenced rifle mesh (M4A1-S): rifle base + extended suppressor barrel */
function buildSilencedRifleMesh(): THREE.Group {
  const g = buildRifleMesh();

  // ---- Suppressor (extends beyond the normal barrel) ----
  // Suppressor body (cylinder)
  part(g, new THREE.CylinderGeometry(0.016, 0.016, 0.12, 8), COL_METAL_MID, 0, 0.03, -0.54, Math.PI / 2, 0, 0);
  // Suppressor front cap
  part(g, new THREE.CylinderGeometry(0.008, 0.008, 0.01, 6), COL_METAL_DARK, 0, 0.03, -0.605, Math.PI / 2, 0, 0);
  // Suppressor rear ring (where it attaches)
  part(g, new THREE.CylinderGeometry(0.018, 0.018, 0.01, 8), COL_METAL_DARK, 0, 0.03, -0.48, Math.PI / 2, 0, 0);

  return g;
}

function buildWeaponMesh(id: WeaponId): THREE.Group {
  const cfg = WEAPONS[id];
  // Special variants
  if (id === 'aug' || id === 'sg553') return buildScopedRifleMesh();
  if (id === 'm4a1s') return buildSilencedRifleMesh();
  switch (cfg.type) {
    case 'pistol':  return buildPistolMesh();
    case 'sniper':  return buildSniperMesh();
    case 'melee':   return buildKnifeMesh();
    case 'smg':     return buildSMGMesh();
    case 'shotgun': return buildShotgunMesh();
    default:        return buildRifleMesh();
  }
}

// ---------------------------------------------------------------------------
// WeaponViewmodel class — two-pass rendering
// ---------------------------------------------------------------------------

export class WeaponViewmodel {
  /** Separate scene for viewmodel rendering (prevents wall clipping). */
  readonly vmScene: THREE.Scene;
  /** Viewmodel camera with narrower FOV for better weapon presence. */
  readonly vmCamera: THREE.PerspectiveCamera;

  /** The group containing the active weapon (child of vmCamera). */
  readonly group: THREE.Group;

  private currentWeaponId: WeaponId;
  private activeGroup: THREE.Group;
  private weaponGroups: Map<WeaponId, THREE.Group> = new Map();
  private grenadeGroup: THREE.Group;

  // GLB model loading
  private static gltfLoader: GLTFLoader | null = null;
  /** Tracks which weapons have a loaded GLB model (vs procedural fallback). */
  private loadedGLBWeapons = new Set<WeaponId>();
  /** Tracks weapons currently being loaded (prevents double-loading). */
  private loadingWeapons = new Set<WeaponId>();
  /** AnimationMixer per loaded weapon (only for GLB models with animations). */
  private animationMixers = new Map<WeaponId, THREE.AnimationMixer>();
  /** Animation clips per weapon, keyed by clip name. */
  private animationClips = new Map<WeaponId, Map<string, THREE.AnimationClip>>();
  /** Currently playing animation actions per weapon. */
  private activeActions = new Map<WeaponId, THREE.AnimationAction>();

  // Animation state
  private bobPhase = 0;
  private time = 0;
  private switching = false;
  private switchPhase: 'lowering' | 'raising' = 'lowering';
  private switchProgress = 0;
  private pendingWeaponId: WeaponId | null = null;

  // Per-weapon recoil state (driven by fireRecoil)
  private recoilPosZ = 0;
  private recoilPosY = 0;
  private recoilRotX = 0;
  private recoilRecovery = 0.15;

  // Spring-based visual recoil overlay (driven by RecoilKickSystem in game.ts).
  // Layered on top of the per-shot recoilPos*/recoilRotX state for extra juice.
  private visualRecoilPosZ = 0;
  private visualRecoilRotX = 0;

  // Reload animation state
  private reloadAnimating = false;
  private reloadPhase = 0;        // 0-4 (five phases)
  private reloadTimer = 0;
  private reloadWeaponType: WeaponCategory = 'rifle';
  /** True when a GLB reload animation is driving the reload — suppresses procedural offsets. */
  private glbReloadActive = false;

  // Jump / land state
  private isAirborne = false;
  private landImpactTimer = -1;    // negative = inactive

  // Crouch state
  private crouchAmount = 0;        // 0 = standing, 1 = fully crouched
  private crouchTarget = 0;

  // Slide state
  private slideAmount = 0;         // 0 = not sliding, 1 = fully sliding
  private slideTarget = 0;

  // Grenade viewmodel state
  private showingGrenade = false;
  // Grenade throw animation state machine
  private grenadeThrowActive = false;
  private grenadeThrowTimer = 0;
  private grenadeThrowPhase: 'pullback' | 'forward' | 'released' = 'pullback';
  /** Set to true at release point — caller checks and clears to know grenade left hand. */
  grenadeReleased = false;

  // Weapon skin state
  private skinLoader: WeaponSkinLoader | null = null;
  private weaponLoadout: WeaponLoadout = { equipped: {} };

  // Weapon inspection (F key)
  readonly inspector = new WeaponInspector();

  // StatTrak display
  private statTrakDisplay: StatTrakDisplay | null = null;

  /**
   * C4b: Sticker / name-tag renderer for first-person viewmodel weapons.
   * Mirrors the marketplace preview path so equipped stickers actually appear
   * during play. `applyStickers` is invoked from `applyWeaponSkin` after the
   * skin textures are bound to the mesh.
   */
  readonly stickerRenderer = new StickerRenderer();

  constructor(_camera: THREE.Camera, initialWeapon: WeaponId = 'glock') {
    // Create separate scene + camera for viewmodel
    this.vmScene = new THREE.Scene();

    // Narrower FOV (65) makes weapon look less distorted
    this.vmCamera = new THREE.PerspectiveCamera(
      65,
      window.innerWidth / window.innerHeight,
      0.01,
      10,
    );
    this.vmScene.add(this.vmCamera);

    // Lighting for viewmodel scene
    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    this.vmScene.add(ambient);
    const dirLight = new THREE.DirectionalLight(0xfff4e0, 0.8);
    dirLight.position.set(1, 2, 1);
    this.vmScene.add(dirLight);
    // Fill light from below-left for hand visibility
    const fillLight = new THREE.DirectionalLight(0xc0c0ff, 0.3);
    fillLight.position.set(-1, -0.5, 0.5);
    this.vmScene.add(fillLight);

    this.group = new THREE.Group();
    this.vmCamera.add(this.group);

    // Build only the initial weapon on-demand (others built lazily in switchWeapon)
    this.activeGroup = this.buildAndRegisterWeapon(initialWeapon);
    this.activeGroup.visible = true;
    this.activeGroup.position.copy(VIEW_OFFSET);

    // Build grenade viewmodel mesh (shared for all grenade types)
    this.grenadeGroup = buildGrenadeMesh();
    this.grenadeGroup.visible = false;
    this.group.add(this.grenadeGroup);

    this.currentWeaponId = initialWeapon;

    // NOTE: resize is handled centrally in game.ts
  }

  /** Build a weapon mesh, register it in weaponGroups, and add to the group. */
  private buildAndRegisterWeapon(id: WeaponId): THREE.Group {
    const wg = buildWeaponMesh(id);
    wg.visible = false;
    this.group.add(wg);
    this.weaponGroups.set(id, wg);
    return wg;
  }

  // -------------------------------------------------------------------------
  // GLB Model Loading
  // -------------------------------------------------------------------------

  /** Get or create the shared GLTFLoader instance. */
  private static getLoader(): GLTFLoader {
    if (!WeaponViewmodel.gltfLoader) {
      WeaponViewmodel.gltfLoader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        'https://www.gstatic.com/draco/versioned/decoders/1.5.7/',
      );
      WeaponViewmodel.gltfLoader.setDRACOLoader(dracoLoader);
    }
    return WeaponViewmodel.gltfLoader;
  }

  /**
   * Attempt to load a GLB model for a weapon. On success, replaces the
   * procedural mesh children with the loaded model. On failure, the
   * procedural fallback stays in place (silent degradation).
   */
  async loadWeaponGLB(id: WeaponId): Promise<void> {
    const modelCfg = WEAPON_MODELS[id];
    if (!modelCfg) return; // no GLB configured for this weapon

    // Already loaded or in-progress
    if (this.loadedGLBWeapons.has(id) || this.loadingWeapons.has(id)) return;

    this.loadingWeapons.add(id);

    try {
      const loader = WeaponViewmodel.getLoader();
      const gltf = await loader.loadAsync(modelCfg.glbPath);

      // Ensure the weapon group still exists (might have been disposed)
      const weaponGroup = this.weaponGroups.get(id);
      if (!weaponGroup) {
        this.loadingWeapons.delete(id);
        return;
      }

      // Keep the procedural arm meshes and replace only the weapon body.
      removeWeaponChildren(weaponGroup);

      // Add loaded model
      const model = gltf.scene;
      const bounds = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3());
      const resolvedScale = resolveWeaponModelScale(id, bounds, modelCfg.scale);
      const resolvedRotation = resolveWeaponModelRotation(bounds, modelCfg.rotationOffset);

      model.scale.setScalar(resolvedScale);
      model.position.set(...modelCfg.positionOffset);
      model.rotation.set(...resolvedRotation);
      model.userData[VIEWMODEL_PART_KEY] = 'weapon-model';
      weaponGroup.add(model);

      this.loadedGLBWeapons.add(id);

      // Set up AnimationMixer if the GLB has animations
      if (gltf.animations.length > 0) {
        const mixer = new THREE.AnimationMixer(model);
        this.animationMixers.set(id, mixer);

        const clips = new Map<string, THREE.AnimationClip>();
        for (const clip of gltf.animations) {
          clips.set(clip.name, clip);
        }
        this.animationClips.set(id, clips);
      }
    } catch (_err) {
      // Silent fallback — keep procedural mesh
      console.warn(`[Viewmodel] GLB load failed for ${id}, keeping procedural fallback`);
    } finally {
      this.loadingWeapons.delete(id);
    }
  }

  /**
   * Play a named animation clip for a weapon (e.g. 'fire', 'reload', 'draw').
   * Only works for weapons with loaded GLB models that have animations.
   *
   * @param targetDurationSec — if provided, the clip's timeScale is adjusted
   *   so the animation plays for exactly this many seconds instead of its
   *   native duration.  Used to sync reload/fire animations with gameplay timing.
   */
  playWeaponAnimation(id: WeaponId, clipName: string, targetDurationSec?: number): void {
    const mixer = this.animationMixers.get(id);
    const clips = this.animationClips.get(id);
    if (!mixer || !clips) return;

    const modelCfg = WEAPON_MODELS[id];
    if (!modelCfg?.animations) return;

    // Resolve the clip name through the model config (e.g. fire -> 'fire_anim')
    const animCfg = modelCfg.animations as Record<string, string | undefined>;
    const actualClipName = animCfg[clipName] ?? clipName;
    const clip = clips.get(actualClipName);
    if (!clip) return;

    // Stop any currently playing action for this weapon
    const prevAction = this.activeActions.get(id);
    if (prevAction) {
      prevAction.fadeOut(0.1);
    }

    const action = mixer.clipAction(clip);
    action.reset();
    action.clampWhenFinished = true;

    // Scale animation speed to match target duration when provided
    if (targetDurationSec && targetDurationSec > 0 && clip.duration > 0) {
      action.timeScale = clip.duration / targetDurationSec;
    }

    // One-shot animations (fire, draw, holster, inspect) play once
    // Idle and reload loop differently
    if (clipName === 'idle') {
      action.setLoop(THREE.LoopRepeat, Infinity);
    } else {
      action.setLoop(THREE.LoopOnce, 1);
    }

    action.fadeIn(0.1);
    action.play();
    this.activeActions.set(id, action);
  }

  /**
   * Tick all active AnimationMixers. Call once per frame from the update loop.
   */
  updateAnimations(delta: number): void {
    for (const mixer of this.animationMixers.values()) {
      mixer.update(delta);
    }
  }

  /**
   * Dispose a loaded GLB model for a weapon, reverting to procedural mesh.
   */
  disposeWeaponModel(id: WeaponId): void {
    // Stop animation
    const action = this.activeActions.get(id);
    if (action) action.stop();
    this.activeActions.delete(id);

    // Dispose mixer
    const mixer = this.animationMixers.get(id);
    if (mixer) {
      mixer.stopAllAction();
      mixer.uncacheRoot(mixer.getRoot());
    }
    this.animationMixers.delete(id);
    this.animationClips.delete(id);

    // Remove GLB model from weapon group and rebuild procedural
    const weaponGroup = this.weaponGroups.get(id);
    if (weaponGroup) {
      removeWeaponChildren(weaponGroup);

      // Rebuild only the procedural weapon body; keep the existing arm meshes.
      const procedural = buildWeaponMesh(id);
      for (const child of [...procedural.children]) {
        if (isViewmodelArmPart(child)) {
          disposeViewmodelChild(child);
          continue;
        }
        weaponGroup.add(child);
      }
    }

    this.loadedGLBWeapons.delete(id);
  }

  /**
   * Preload weapons based on priority. Call from game init.
   * - 'blocking' weapons are awaited (must complete before game starts)
   * - 'eager' weapons start loading in background (non-blocking)
   *
   * Returns a promise that resolves when all blocking weapons are loaded.
   */
  async preloadWeapons(): Promise<void> {
    // Load blocking weapons (await all)
    const blockingIds = getWeaponsByPriority('blocking');
    const blockingPromises = blockingIds.map((id) => {
      // Ensure procedural mesh exists first
      if (!this.weaponGroups.has(id)) {
        this.buildAndRegisterWeapon(id);
      }
      return this.loadWeaponGLB(id);
    });

    await Promise.all(blockingPromises);

    // Kick off eager loading (non-blocking)
    const eagerIds = getWeaponsByPriority('eager');
    for (const id of eagerIds) {
      if (!this.weaponGroups.has(id)) {
        this.buildAndRegisterWeapon(id);
      }
      // Fire-and-forget: don't await
      this.loadWeaponGLB(id);
    }
  }

  /**
   * Check if a weapon has a loaded GLB model (vs procedural fallback).
   */
  hasLoadedModel(id: WeaponId): boolean {
    return this.loadedGLBWeapons.has(id);
  }

  /** Update viewmodel camera aspect ratio. Called from the central resize handler. */
  handleResize(width: number, height: number): void {
    this.vmCamera.aspect = width / height;
    this.vmCamera.updateProjectionMatrix();
  }

  /** Set the weapon skin loader and loadout. Called from game init. */
  setWeaponSkinSystem(loader: WeaponSkinLoader, loadout: WeaponLoadout): void {
    this.skinLoader = loader;
    this.weaponLoadout = loadout;
  }

  // -------------------------------------------------------------------------
  // Inspection (F key)
  // -------------------------------------------------------------------------

  /** Toggle weapon inspection mode (F key). */
  toggleInspect(): void {
    if (this.showingGrenade || this.switching || this.reloadAnimating) return;
    this.inspector.toggleInspect(this.activeGroup);
  }

  /** Feed mouse movement to the inspector during inspection. */
  inspectMouseMove(dx: number, dy: number): void {
    this.inspector.onMouseMove(dx, dy);
  }

  /** Feed scroll to the inspector during inspection. */
  inspectScroll(delta: number): void {
    this.inspector.onScroll(delta);
  }

  // -------------------------------------------------------------------------
  // StatTrak
  // -------------------------------------------------------------------------

  /** Attach a StatTrak display to the current weapon. */
  attachStatTrak(killCount: number): void {
    this.detachStatTrak();
    this.statTrakDisplay = new StatTrakDisplay();
    const weaponGroup = this.weaponGroups.get(this.currentWeaponId);
    if (weaponGroup) {
      this.statTrakDisplay.create(weaponGroup, this.currentWeaponId, killCount);
    }
  }

  /** Remove the StatTrak display from the current weapon. */
  detachStatTrak(): void {
    if (this.statTrakDisplay) {
      this.statTrakDisplay.dispose();
      this.statTrakDisplay = null;
    }
  }

  /** Increment the StatTrak kill counter by 1. */
  statTrakKill(): void {
    this.statTrakDisplay?.incrementKill();
  }

  /** F-CM-007: re-attach or detach the StatTrak display based on the loadout's
   *  per-weapon kill count for the currently-equipped weapon. Called from the
   *  applyWeaponSkin path so weapon switches refresh the display. The kill-event
   *  handler should mutate `weaponLoadout.statTrakKills[weaponId]` and then call
   *  `statTrakKill()` to animate the increment without re-creating the mesh. */
  refreshStatTrakForCurrentWeapon(): void {
    const kills = this.weaponLoadout.statTrakKills?.[this.currentWeaponId];
    if (typeof kills === 'number' && kills >= 0) {
      this.attachStatTrak(kills);
    } else {
      this.detachStatTrak();
    }
  }

  /** Apply the equipped skin to a weapon's mesh group. */
  async applyWeaponSkin(weaponId: WeaponId): Promise<void> {
    // F-CM-007: refresh StatTrak first so the LED display tracks weapon switches
    // even when no skin is equipped (StatTrak is independent of skin choice).
    this.refreshStatTrakForCurrentWeapon();

    if (!this.skinLoader) return;

    const skinId = this.weaponLoadout.equipped[weaponId] ?? 0;
    const weaponGroup = this.weaponGroups.get(weaponId);
    if (!weaponGroup) return;

    if (skinId === 0) {
      this.skinLoader.removeSkin(weaponGroup);
    } else {
      const skin = weaponSkinRegistry.getSkin(skinId);
      if (skin) {
        try {
          const textures = await this.skinLoader.loadSkinTextures(skin);
          if (!this.weaponGroups.has(weaponId)) return;
          // Resolve the wear float for this equipped instance. If the loadout
          // didn't carry per-weapon wear (no instance store wired yet), fall
          // back to the midpoint of the skin's wearRange so the wear shader
          // renders visible wear rather than always Factory-New (F-CM-001).
          const instanceWear = this.weaponLoadout.wearFloats?.[weaponId];
          const fallbackWear =
            skin.wearRange[0] + (skin.wearRange[1] - skin.wearRange[0]) * 0.5;
          const wearFloat = instanceWear ?? fallbackWear;
          this.skinLoader.applySkinToMesh(weaponGroup, textures, skin, wearFloat);
        } catch (err) {
          console.warn(`[Viewmodel] Failed to apply skin ${skinId} to ${weaponId}:`, err);
        }
      }
    }

    // C4b: project equipped stickers/name tag onto the viewmodel mesh after the
    // skin textures have been bound. applyStickers/applyNameTag are no-ops when
    // the slot list is empty, so the call is safe whether the loadout has any
    // cosmetics for this weapon or not.
    const stickers = this.weaponLoadout.stickers?.[weaponId];
    if (stickers && stickers.length > 0) {
      try {
        await this.stickerRenderer.applyStickers(weaponGroup, weaponId, stickers, STICKER_MANIFEST);
      } catch (err) {
        console.warn(`[Viewmodel] Failed to apply stickers to ${weaponId}:`, err);
      }
    } else {
      this.stickerRenderer.removeStickers(weaponGroup);
    }
    const tag = this.weaponLoadout.nameTags?.[weaponId];
    if (tag) {
      this.stickerRenderer.applyNameTag(weaponGroup, tag);
    } else {
      this.stickerRenderer.removeNameTag(weaponGroup);
    }
  }

  /**
   * Sync viewmodel camera rotation to match the main camera.
   * Call before render each frame.
   */
  syncCamera(mainCamera: THREE.Camera): void {
    this.vmCamera.quaternion.copy(mainCamera.quaternion);
  }

  /**
   * Render the viewmodel on top of the world pass.
   * Clears only the depth buffer so the weapon always draws on top.
   */
  render(renderer: THREE.WebGLRenderer): void {
    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(this.vmScene, this.vmCamera);
    renderer.autoClear = oldAutoClear;
  }

  update(dt: number, isMoving: boolean): void {
    this.time += dt;

    // --- Weapon bob when moving ---
    if (isMoving) {
      this.bobPhase += dt * BOB_SPEED;
    } else {
      this.bobPhase += dt * 2;
    }

    const bobX = isMoving ? Math.sin(this.bobPhase) * BOB_AMOUNT_X : 0;
    const bobY = isMoving ? Math.sin(this.bobPhase * 2) * BOB_AMOUNT_Y : 0;

    // --- Idle sway (subtle weapon movement when standing still) ---
    const swayX = !isMoving ? Math.sin(this.time * SWAY_SPEED_X) * SWAY_AMOUNT_X : 0;
    const swayY = !isMoving ? Math.sin(this.time * SWAY_SPEED_Y) * SWAY_AMOUNT_Y : 0;

    // --- Recoil recovery (spring-damper toward rest) ---
    this.recoilPosZ *= (1 - this.recoilRecovery);
    this.recoilPosY *= (1 - this.recoilRecovery);
    this.recoilRotX *= (1 - this.recoilRecovery);

    // Kill tiny values
    if (Math.abs(this.recoilPosZ) < 0.0001) this.recoilPosZ = 0;
    if (Math.abs(this.recoilRotX) < 0.0001) this.recoilRotX = 0;

    // --- Switch animation ---
    let switchDrop = 0;
    if (this.switching) {
      this.switchProgress += dt * SWITCH_SPEED;

      if (this.switchPhase === 'lowering') {
        switchDrop = Math.min(this.switchProgress, 1) * SWITCH_DROP;
        if (this.switchProgress >= 1) {
          this.activeGroup.visible = false;
          if (this.pendingWeaponId) {
            this.currentWeaponId = this.pendingWeaponId;
            this.activeGroup = this.weaponGroups.get(this.currentWeaponId)!;
            this.pendingWeaponId = null;
          }
          this.activeGroup.visible = true;
          this.switchPhase = 'raising';
          this.switchProgress = 0;

          // Play GLB draw animation on the newly active weapon
          if (this.loadedGLBWeapons.has(this.currentWeaponId)) {
            this.playWeaponAnimation(this.currentWeaponId, 'draw');
          }
          // Apply skin to newly visible weapon
          this.applyWeaponSkin(this.currentWeaponId);
        }
      }

      if (this.switchPhase === 'raising') {
        switchDrop = (1 - Math.min(this.switchProgress, 1)) * SWITCH_DROP;
        if (this.switchProgress >= 1) {
          this.switching = false;
          this.switchProgress = 0;
        }
      }
    }

    // --- Reload animation ---
    let reloadPosX = 0, reloadPosY = 0, reloadPosZ = 0;
    let reloadRotX = 0, reloadRotY = 0, reloadRotZ = 0;

    if (this.reloadAnimating) {
      this.reloadTimer += dt;
      const timings = RELOAD_TIMINGS[this.reloadWeaponType];

      // Advance phase based on timer
      while (this.reloadPhase < 4 && this.reloadTimer >= timings[this.reloadPhase]) {
        this.reloadPhase++;
      }

      // Check if animation is complete
      if (this.reloadTimer >= timings[4]) {
        this.reloadAnimating = false;
        this.reloadTimer = 0;
        this.reloadPhase = 0;
        this.glbReloadActive = false;
      } else if (!this.glbReloadActive) {
        // Only compute procedural offsets when no GLB animation is driving
        const offsets = this.computeReloadOffsets();
        reloadPosX = offsets.posX;
        reloadPosY = offsets.posY;
        reloadPosZ = offsets.posZ;
        reloadRotX = offsets.rotX;
        reloadRotY = offsets.rotY;
        reloadRotZ = offsets.rotZ;
      }
      // When glbReloadActive is true, the AnimationMixer drives the model
      // directly — procedural offsets stay at zero.
    }

    // --- Jump / Land offsets ---
    let airPosY = 0;
    let airRotX = 0;

    if (this.isAirborne) {
      // In air: weapon drifts down and tilts forward
      airPosY = -AIRBORNE_DROP;
      airRotX = AIRBORNE_TILT;
    }

    // Land impact: quick downward jolt that recovers with spring-damper
    let landPosY = 0;
    if (this.landImpactTimer >= 0) {
      this.landImpactTimer += dt;
      if (this.landImpactTimer >= LAND_RECOVER_DURATION) {
        this.landImpactTimer = -1; // done
      } else {
        const recoverT = this.landImpactTimer / LAND_RECOVER_DURATION;
        // Quick drop then spring recovery
        const impact = LAND_IMPACT_Y * (1 - smoothstep(recoverT));
        landPosY = impact;
      }
    }

    // --- Crouch offset (smooth lerp) ---
    this.crouchAmount = lerp(this.crouchAmount, this.crouchTarget, Math.min(1, CROUCH_LERP_RATE * dt));
    // Kill tiny drift
    if (Math.abs(this.crouchAmount - this.crouchTarget) < 0.001) this.crouchAmount = this.crouchTarget;

    const crouchPosY = -CROUCH_DROP_Y * this.crouchAmount;
    const crouchPosZ = CROUCH_FORWARD_Z * this.crouchAmount;

    // --- Slide offset (smooth lerp) ---
    this.slideAmount = lerp(this.slideAmount, this.slideTarget, Math.min(1, SLIDE_LERP_RATE * dt));
    if (Math.abs(this.slideAmount - this.slideTarget) < 0.001) this.slideAmount = this.slideTarget;

    const slidePosX = SLIDE_SHIFT_X * this.slideAmount;
    const slidePosY = -SLIDE_DROP_Y * this.slideAmount;
    const slideRotZ = SLIDE_TILT_Z * this.slideAmount;

    // --- Grenade throw animation ---
    let throwPosY = 0, throwPosZ = 0, throwRotX = 0;
    if (this.grenadeThrowActive) {
      this.grenadeThrowTimer += dt;
      const totalPullForward = GRENADE_PULLBACK_DURATION + GRENADE_FORWARD_DURATION;

      if (this.grenadeThrowPhase === 'pullback') {
        // Phase 1: Hand pulls back (wind-up)
        const t = smoothstep(Math.min(this.grenadeThrowTimer / GRENADE_PULLBACK_DURATION, 1));
        throwPosZ = GRENADE_PULLBACK_Z * t;
        throwPosY = GRENADE_PULLBACK_Y * t;
        throwRotX = GRENADE_PULLBACK_ROT_X * t;
        if (this.grenadeThrowTimer >= GRENADE_PULLBACK_DURATION) {
          this.grenadeThrowPhase = 'forward';
        }
      }

      if (this.grenadeThrowPhase === 'forward') {
        // Phase 2: Hand lunges forward (throw)
        const elapsed = this.grenadeThrowTimer - GRENADE_PULLBACK_DURATION;
        const t = smoothstep(Math.min(elapsed / GRENADE_FORWARD_DURATION, 1));
        // Interpolate from pullback position to forward position
        throwPosZ = lerp(GRENADE_PULLBACK_Z, GRENADE_FORWARD_Z, t);
        throwPosY = lerp(GRENADE_PULLBACK_Y, GRENADE_FORWARD_Y, t);
        throwRotX = lerp(GRENADE_PULLBACK_ROT_X, GRENADE_FORWARD_ROT_X, t);
        if (this.grenadeThrowTimer >= totalPullForward) {
          // Release point — grenade leaves hand
          this.grenadeThrowPhase = 'released';
          this.grenadeReleased = true;
          this.grenadeGroup.visible = false; // Hide grenade from hand
        }
      }

      if (this.grenadeThrowPhase === 'released') {
        // Phase 3: Follow-through (hand relaxes back to rest)
        const elapsed = this.grenadeThrowTimer - totalPullForward;
        const t = smoothstep(Math.min(elapsed / GRENADE_FOLLOW_DURATION, 1));
        throwPosZ = lerp(GRENADE_FORWARD_Z, 0, t);
        throwPosY = lerp(GRENADE_FORWARD_Y, 0, t);
        throwRotX = lerp(GRENADE_FORWARD_ROT_X, 0, t);
        if (elapsed >= GRENADE_FOLLOW_DURATION) {
          this.grenadeThrowActive = false;
          this.grenadeThrowTimer = 0;
        }
      }
    }

    // --- Composite: apply all offsets ---
    const targetGroup = this.showingGrenade ? this.grenadeGroup : this.activeGroup;
    targetGroup.position.set(
      VIEW_OFFSET.x + bobX + swayX + reloadPosX + slidePosX,
      VIEW_OFFSET.y + bobY + swayY - switchDrop + this.recoilPosY + reloadPosY + airPosY + landPosY + crouchPosY + slidePosY + throwPosY,
      VIEW_OFFSET.z + this.recoilPosZ + this.visualRecoilPosZ + reloadPosZ + crouchPosZ + throwPosZ,
    );

    targetGroup.rotation.set(
      -this.recoilRotX - this.visualRecoilRotX + reloadRotX + airRotX + throwRotX,
      reloadRotY,
      reloadRotZ + slideRotZ,
    );

    // Tick GLB animation mixers
    this.updateAnimations(dt);

    // Tick inspection animation (overrides position/rotation when active)
    if (this.inspector.isInspecting) {
      this.inspector.update(dt);
    }

    // Tick StatTrak display animation
    this.statTrakDisplay?.update(dt);

    // C4b: tick holographic sticker shaders so iridescence animates while
    // worn in-match (mirrors marketplace preview behaviour).
    this.stickerElapsed += dt;
    this.stickerRenderer.update(this.stickerElapsed);
  }
  private stickerElapsed = 0;

  /**
   * Apply recoil kick from weapon system.
   * Uses per-weapon RecoilKick parameters for different feel per gun.
   * Also plays the GLB fire animation if a model is loaded.
   */
  fireRecoil(kick?: RecoilKick): void {
    if (kick) {
      this.recoilPosZ += kick.posZ;
      this.recoilPosY += kick.posY;
      this.recoilRotX += kick.rotX;
      this.recoilRecovery = kick.recovery;
    } else {
      // Fallback: generic recoil
      this.recoilPosZ += 0.03;
      this.recoilPosY += 0.02;
      this.recoilRotX += 0.06;
      this.recoilRecovery = 0.15;
    }

    // Play GLB fire animation if loaded
    if (this.loadedGLBWeapons.has(this.currentWeaponId)) {
      this.playWeaponAnimation(this.currentWeaponId, 'fire');
    }
  }

  /**
   * Set spring-based visual recoil overlay offsets (driven by RecoilKickSystem).
   * Overwritten each frame — composited into targetGroup position/rotation in
   * update(). Does NOT replace per-shot instant-snap recoil; layers on top.
   */
  setVisualRecoilOffset(posZ: number, rotX: number): void {
    this.visualRecoilPosZ = posZ;
    this.visualRecoilRotX = rotX;
  }

  // -------------------------------------------------------------------------
  // Reload animation
  // -------------------------------------------------------------------------

  /**
   * Start a reload animation.  When the current weapon has a loaded GLB model
   * with a 'reload' clip, the clip drives the animation (duration scaled to
   * match the weapon's reloadTimeMs from config).  Otherwise, the procedural
   * multi-phase animation plays as fallback.
   */
  startReloadAnimation(weaponType: WeaponCategory): void {
    if (this.reloadAnimating) return; // already reloading
    this.reloadAnimating = true;
    this.reloadTimer = 0;
    this.reloadPhase = 0;
    this.reloadWeaponType = weaponType;

    // Play GLB reload animation if loaded, with duration scaled to weapon config
    if (this.loadedGLBWeapons.has(this.currentWeaponId)) {
      const weaponCfg = WEAPONS[this.currentWeaponId];
      const reloadTimeSec = weaponCfg ? weaponCfg.reloadTimeMs / 1000 : undefined;
      this.playWeaponAnimation(this.currentWeaponId, 'reload', reloadTimeSec);
      this.glbReloadActive = true;
    } else {
      this.glbReloadActive = false;
    }
  }

  /**
   * Compute reload offsets for the current phase and timer.
   * Returns { posX, posY, posZ, rotX, rotY, rotZ } offsets.
   */
  private computeReloadOffsets(): { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number } {
    const timings = RELOAD_TIMINGS[this.reloadWeaponType];
    const phaseStart = this.reloadPhase === 0 ? 0 : timings[this.reloadPhase - 1];
    const phaseEnd = timings[this.reloadPhase];
    const phaseDuration = phaseEnd - phaseStart;
    const phaseT = smoothstep(Math.min(1, (this.reloadTimer - phaseStart) / phaseDuration));

    switch (this.reloadWeaponType) {
      case 'pistol':
        return this.computePistolReload(this.reloadPhase, phaseT);
      case 'rifle':
        return this.computeRifleReload(this.reloadPhase, phaseT);
      case 'sniper':
        return this.computeSniperReload(this.reloadPhase, phaseT);
      case 'smg':
        return this.computeRifleReload(this.reloadPhase, phaseT); // SMG uses rifle-style reload
      case 'shotgun':
        return this.computeShotgunReload(this.reloadPhase, phaseT);
      case 'melee':
        // Knife has no reload — return zero offsets
        return { posX: 0, posY: 0, posZ: 0, rotX: 0, rotY: 0, rotZ: 0 };
    }
  }

  private computePistolReload(phase: number, t: number): { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number } {
    let posX = 0, posY = 0, posZ = 0;
    let rotX = 0, rotY = 0, rotZ = 0;

    switch (phase) {
      case 0: // Tilt right, drop magazine
        rotZ = lerp(0, -0.26, t);  // ~15° right tilt
        posY = lerp(0, -0.15 * t, t); // mag drop hint (weapon shifts slightly)
        break;
      case 1: // Hand reaches down, weapon stays tilted with slight downward shift
        rotZ = -0.26;
        posY = lerp(-0.15, -0.04, t); // slight sag
        posZ = lerp(0, 0.02, t);      // slight forward shift
        break;
      case 2: // Insert magazine, weapon straightens
        rotZ = lerp(-0.26, 0, t);
        posY = lerp(-0.04, 0, t);
        posZ = lerp(0.02, 0, t);
        break;
      case 3: // Chamber rack — slide pull back then forward
        {
          const halfT = t < 0.5 ? t * 2 : (1 - t) * 2; // 0→1→0
          posZ = lerp(0, 0.04, halfT); // slide travel
          rotX = lerp(0, -0.03, halfT);
        }
        break;
      case 4: // Return to idle
        // Everything already at 0, just smooth any residual
        break;
    }
    return { posX, posY, posZ, rotX, rotY, rotZ };
  }

  private computeRifleReload(phase: number, t: number): { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number } {
    let posX = 0, posY = 0, posZ = 0;
    let rotX = 0, rotY = 0, rotZ = 0;

    switch (phase) {
      case 0: // Tilt left ~10°, push mag release
        rotZ = lerp(0, 0.175, t);    // ~10° left tilt
        posX = lerp(0, -0.02, t);
        break;
      case 1: // Old mag drops out, left hand grabs new one
        rotZ = 0.175;
        posX = -0.02;
        posY = lerp(0, -0.05, t);    // weapon sags as mag drops
        rotX = lerp(0, 0.04, t);     // slight forward tilt
        break;
      case 2: // New magazine rocks in (up with slight rotation)
        rotZ = lerp(0.175, 0.08, t); // straightening
        posY = lerp(-0.05, -0.01, t);
        rotX = lerp(0.04, 0.02, t);
        posX = lerp(-0.02, -0.01, t);
        break;
      case 3: // Slap bolt catch / pull charging handle
        {
          const hitT = t < 0.3 ? t / 0.3 : 1; // quick slap
          rotZ = lerp(0.08, 0.02, t);
          posY = lerp(-0.01, 0.01 * (1 - hitT), t);
          rotX = lerp(0.02, -0.02 * smoothstep(hitT), 1);
          posX = lerp(-0.01, 0, t);
        }
        break;
      case 4: // Return to position
        rotZ = lerp(0.02, 0, t);
        rotX = lerp(-0.02, 0, t);
        posY = lerp(0, 0, t);
        break;
    }
    return { posX, posY, posZ, rotX, rotY, rotZ };
  }

  private computeSniperReload(phase: number, t: number): { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number } {
    let posX = 0, posY = 0, posZ = 0;
    let rotX = 0, rotY = 0, rotZ = 0;

    switch (phase) {
      case 0: // Rotate weapon exposing bolt, tilt right
        rotZ = lerp(0, -0.2, t);     // right tilt
        rotY = lerp(0, 0.1, t);      // rotate to expose bolt
        posY = lerp(0, -0.02, t);
        break;
      case 1: // Pull bolt back (longer travel), eject round
        rotZ = -0.2;
        rotY = 0.1;
        posY = -0.02;
        posZ = lerp(0, 0.06, t);     // bolt pull back
        rotX = lerp(0, -0.04, t);    // slight upward tilt from bolt pull
        break;
      case 2: // Push magazine in
        rotZ = -0.2;
        rotY = lerp(0.1, 0.05, t);
        posZ = lerp(0.06, 0.03, t);  // partially return
        posY = lerp(-0.02, -0.04, t); // sag from mag insertion
        rotX = lerp(-0.04, -0.02, t);
        break;
      case 3: // Push bolt forward, lock down
        {
          const lockT = t < 0.7 ? t / 0.7 : 1;
          rotZ = lerp(-0.2, -0.05, t);
          rotY = lerp(0.05, 0.01, t);
          posZ = lerp(0.03, 0, lockT);
          posY = lerp(-0.04, -0.01, t);
          rotX = lerp(-0.02, 0, t);
        }
        break;
      case 4: // Return to position
        rotZ = lerp(-0.05, 0, t);
        rotY = lerp(0.01, 0, t);
        posY = lerp(-0.01, 0, t);
        break;
    }
    return { posX, posY, posZ, rotX, rotY, rotZ };
  }

  private computeShotgunReload(phase: number, t: number): { posX: number; posY: number; posZ: number; rotX: number; rotY: number; rotZ: number } {
    let posX = 0, posY = 0, posZ = 0;
    let rotX = 0, rotY = 0, rotZ = 0;

    switch (phase) {
      case 0: // Tilt weapon to expose loading port
        rotZ = lerp(0, -0.15, t);
        rotX = lerp(0, 0.05, t);
        posY = lerp(0, -0.02, t);
        break;
      case 1: // Hand reaches for shell
        rotZ = -0.15;
        rotX = 0.05;
        posY = lerp(-0.02, -0.03, t);
        posX = lerp(0, -0.01, t);
        break;
      case 2: // Insert shell
        rotZ = -0.15;
        rotX = lerp(0.05, 0.03, t);
        posY = lerp(-0.03, -0.02, t);
        posX = -0.01;
        break;
      case 3: // Pump action (forward-back)
        {
          const pumpT = t < 0.5 ? t * 2 : (1 - t) * 2;
          rotZ = lerp(-0.15, -0.05, t);
          posZ = lerp(0, 0.04, pumpT);
          posY = lerp(-0.02, 0, t);
          rotX = lerp(0.03, 0, t);
        }
        break;
      case 4: // Return to idle
        rotZ = lerp(-0.05, 0, t);
        posX = lerp(-0.01, 0, t);
        break;
    }
    return { posX, posY, posZ, rotX, rotY, rotZ };
  }

  // -------------------------------------------------------------------------
  // Jump / Land
  // -------------------------------------------------------------------------

  /**
   * Set airborne state. When transitioning from airborne to grounded,
   * triggers a landing impact animation.
   */
  setAirborne(airborne: boolean): void {
    if (this.isAirborne && !airborne) {
      // Just landed — trigger impact
      this.landImpactTimer = 0;
    }
    this.isAirborne = airborne;
  }

  // -------------------------------------------------------------------------
  // Crouch
  // -------------------------------------------------------------------------

  /** Set crouching state. Viewmodel smoothly interpolates to crouch offset. */
  setCrouching(crouching: boolean): void {
    this.crouchTarget = crouching ? 1 : 0;
  }

  // -------------------------------------------------------------------------
  // Slide
  // -------------------------------------------------------------------------

  /** Set sliding state. Viewmodel tilts and drops dynamically. */
  setSliding(sliding: boolean): void {
    this.slideTarget = sliding ? 1 : 0;
  }

  /** Show the grenade viewmodel instead of the current weapon. */
  showGrenadeViewmodel(): void {
    if (this.showingGrenade) return;
    this.showingGrenade = true;
    this.activeGroup.visible = false;
    this.grenadeGroup.visible = true;
    this.grenadeGroup.position.copy(VIEW_OFFSET);
  }

  /** Hide the grenade viewmodel and show the weapon again. */
  hideGrenadeViewmodel(): void {
    if (!this.showingGrenade) return;
    this.showingGrenade = false;
    this.grenadeGroup.visible = false;
    this.activeGroup.visible = true;
  }

  /** Check if showing grenade viewmodel. */
  isShowingGrenade(): boolean {
    return this.showingGrenade;
  }

  /** Start the grenade throw animation (pullback → forward → release). */
  startGrenadeThrow(): void {
    this.grenadeThrowActive = true;
    this.grenadeThrowTimer = 0;
    this.grenadeThrowPhase = 'pullback';
    this.grenadeReleased = false;
    // Reset recoil so it doesn't conflict
    this.recoilPosZ = 0;
    this.recoilPosY = 0;
    this.recoilRotX = 0;
  }

  /** Whether the grenade throw animation is still playing. */
  isGrenadeThrowActive(): boolean {
    return this.grenadeThrowActive;
  }

  switchWeapon(weaponId: WeaponId): void {
    // Cancel inspection when switching weapons
    if (this.inspector.isInspecting) {
      this.inspector.stopInspect();
    }
    // Hide grenade viewmodel when switching to a weapon
    if (this.showingGrenade) {
      this.hideGrenadeViewmodel();
    }
    if (weaponId === this.currentWeaponId && !this.switching) return;
    // Lazily build weapon mesh if not yet created
    if (!this.weaponGroups.has(weaponId)) {
      this.buildAndRegisterWeapon(weaponId);
    }

    // Trigger lazy GLB loading for weapons not yet loaded
    const modelCfg = WEAPON_MODELS[weaponId];
    if (modelCfg && !this.loadedGLBWeapons.has(weaponId) && !this.loadingWeapons.has(weaponId)) {
      this.loadWeaponGLB(weaponId);
    }
    // Apply weapon skin after build
    this.applyWeaponSkin(weaponId);

    this.pendingWeaponId = weaponId;
    this.switching = true;
    this.switchPhase = 'lowering';
    this.switchProgress = 0;
  }

  getCurrentWeaponId(): WeaponId {
    return this.currentWeaponId;
  }

  /** Hide the viewmodel (e.g. when scoped). */
  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  /**
   * Update viewmodel arm/sleeve/glove colors to match the selected skin.
   * Traverses existing weapon meshes and updates material colors in-place —
   * no geometry rebuild needed for color-only changes.
   */
  setSkinColors(skinColor: number): void {
    // Capture old arm colors before mutation for matching
    const oldSkin = COL_SKIN;
    const oldGlove = COL_GLOVE;
    const oldGlovePalm = COL_GLOVE_PALM;
    const oldSleeve = COL_SLEEVE;

    // Derive arm colors from the skin's primary color
    const cr = (skinColor >> 16) & 0xff;
    const cg = (skinColor >> 8) & 0xff;
    const cb = skinColor & 0xff;

    // Sleeve = skin color darkened slightly
    COL_SLEEVE = darkenColor(skinColor, 0.7);
    // Glove = very dark version of skin color
    COL_GLOVE = darkenColor(skinColor, 0.25);
    COL_GLOVE_PALM = darkenColor(skinColor, 0.18);
    // Skin/wrist = lighter tint blended with original skin tone
    // For non-human skins (very green/blue/etc), use the skin color directly
    const isHumanTone = (cr > 100 && cg > 60 && cb > 30 && cr > cg);
    COL_SKIN = isHumanTone ? 0xd4a574 : lightenColor(skinColor, 1.3);

    // Build old → new color mapping
    const colorMap: Array<[number, number]> = [
      [oldSkin, COL_SKIN],
      [oldGlove, COL_GLOVE],
      [oldGlovePalm, COL_GLOVE_PALM],
      [oldSleeve, COL_SLEEVE],
    ];

    // Traverse all existing weapon meshes and update arm/hand material colors in-place.
    // Supports both MeshLambertMaterial (procedural) and MeshStandardMaterial (GLB).
    // For GLB models, matches materials by name (arm, glove, sleeve) when color-matching fails.
    const updateGroup = (wg: THREE.Group): void => {
      wg.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        const mat = child.material;
        if (!(mat instanceof THREE.Material) || !('color' in mat)) return;
        const colorMat = mat as THREE.MeshLambertMaterial | THREE.MeshStandardMaterial;
        const currentHex = colorMat.color.getHex();
        for (const [oldColor, newColor] of colorMap) {
          if (currentHex === oldColor) {
            colorMat.color.setHex(newColor);
            colorMat.needsUpdate = true;
            break;
          }
        }
        // GLB models: match material by name for skin/glove/sleeve
        if (mat.name) {
          const n = mat.name.toLowerCase();
          if (n.includes('skin') || n.includes('wrist')) {
            colorMat.color.setHex(COL_SKIN);
            colorMat.needsUpdate = true;
          } else if (n.includes('glove') || n.includes('hand')) {
            colorMat.color.setHex(COL_GLOVE);
            colorMat.needsUpdate = true;
          } else if (n.includes('sleeve') || n.includes('arm')) {
            colorMat.color.setHex(COL_SLEEVE);
            colorMat.needsUpdate = true;
          }
        }
      });
    };

    for (const wg of this.weaponGroups.values()) {
      updateGroup(wg);
    }
    // Also update grenade viewmodel
    updateGroup(this.grenadeGroup);
  }

  dispose(): void {
    // Stop and dispose all animation mixers
    for (const [id, mixer] of this.animationMixers) {
      mixer.stopAllAction();
      mixer.uncacheRoot(mixer.getRoot());
    }
    this.animationMixers.clear();
    this.animationClips.clear();
    this.activeActions.clear();
    this.loadedGLBWeapons.clear();
    this.loadingWeapons.clear();
    this.skinLoader = null;
    this.inspector.stopInspect();
    this.detachStatTrak();

    // Dispose weapon group geometries and materials
    for (const wg of this.weaponGroups.values()) {
      wg.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else if (child.material instanceof THREE.Material) {
            child.material.dispose();
          }
        }
      });
    }
  }
}

/** Darken a color by a factor (0..1). */
function darkenColor(color: number, factor: number): number {
  const r = Math.floor(((color >> 16) & 0xff) * factor);
  const g = Math.floor(((color >> 8) & 0xff) * factor);
  const b = Math.floor((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

/** Lighten a color by a factor (>1 to lighten). */
function lightenColor(color: number, factor: number): number {
  const r = Math.min(255, Math.floor(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.floor(((color >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.floor((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}
